import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import { calculateBulkCommission, sendInvoiceLink, verifyInvoiceOtpNormalWallet } from "../../../../../api";
import convertToRupees from "../../../../../utilities/convertToRupees";
import { getFromLocalStorage } from "../../../../../hooks/useLocalStorage";
import GetLoader from "../../../../common/getLoader";
import { USE_V2_WEBSOCKET } from "../../../../../constant";
import usePaymentWebSocket from '../../../../../hooks/usePaymentWebSocket';

const PayBulkInvoiceModal = ({ isOpen, toggle, selectedInvoiceNumbers , selectedBP = null, groupId }) => {
  console.log({ isOpen, toggle, selectedInvoiceNumbers , selectedBP, groupId })
  const [loading, setLoading] = useState(false);
  const [commissionData, setCommissionData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [confirmDetails, setConfirmDetails] = useState(false);

  // Get user and partnership info from local storage
  const user = getFromLocalStorage("user");
  const role_id = getFromLocalStorage("ROLE")?.role_id;
  const role = getFromLocalStorage("ROLE")?.key;
  const partnership_id = role == 'SALES_MANAGER' ? selectedBP : user?.partnership_id;
  console.log("🔍 PayBulkInvoiceModal initialized with:", selectedBP)

  const [txnid, setTxnid] = useState(null); // Used for dynamic WebSocket connection

  // Connect WebSocket dynamically when txnid is available
  const { paymentEvent, clearEvent } = usePaymentWebSocket(txnid, USE_V2_WEBSOCKET && !!txnid);

  useEffect(() => {
      if (paymentEvent) {
          if (paymentEvent.status === 'SUCCESS' || paymentEvent.status?.toLowerCase() === 'completed') {
              setProcessing(false);
              setRedirecting(false);
              toggle(); // Close modal
              
              window.dispatchEvent(new CustomEvent('show-global-payment-modal', {
                detail: {
                  status: 'SUCCESS',
                  message: 'Your wallet was successfully recharged!',
                  documentInfo: paymentEvent.payload?.amount || commissionData?.net_payable_amount || 0,
                  documentLabel: 'Amount Recharged (INR):'
                }
              }));
              
              window.dispatchEvent(new Event('refresh-invoices')); // Instruct parent to refresh
          } else if (paymentEvent.status === 'FAILED' || paymentEvent.status?.toLowerCase() === 'failed') {
              setProcessing(false);
              setRedirecting(false);
              toggle(); // Close modal
              
              window.dispatchEvent(new CustomEvent('show-global-payment-modal', {
                detail: {
                  status: 'FAILED',
                  message: 'Payment failed! Please try again.',
                  documentInfo: paymentEvent.payload?.amount || commissionData?.net_payable_amount || 0,
                  documentLabel: 'Amount Attempted (INR):'
                }
              }));
          }
          clearEvent();
      }
  }, [paymentEvent, toggle, clearEvent, commissionData, txnid]);

  const handleSendLink = async () => {
    try {

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Mobile validation (10 digits)
      const mobileRegex = /^[0-9]{10}$/;

      if (!email.trim()) {
        toast.error("Email is required");
        return;
      }

      if (!emailRegex.test(email.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }

      if (!mobileNumber.trim()) {
        toast.error("Mobile number is required");
        return;
      }

      if (!mobileRegex.test(mobileNumber.trim())) {
        toast.error("Please enter a valid 10-digit mobile number");
        return;
      }

      const payload = {
        "reqEmail": email,
        "reqPhone": mobileNumber,
        // "invoice_ids": selectedInvoiceNumbers,
        "role_id": role_id,
        "isNormalWallet": true,
        "groupId": groupId,
        "invoiceNumbers": selectedInvoiceNumbers
      };

      console.log('payload', payload);
      const response = await sendInvoiceLink(payload);
      // console.log(response, "sendLink response");

      // Close modal
      toggle();

      if (response?.data?.code === 200) {
        toast.success("Payment link sent successfully");
      }
    } catch (error) {
      toast.error("Failed to send payment link");
    }
  };

  useEffect(() => {
    if (isOpen && selectedInvoiceNumbers?.length > 0) {
      fetchCommissionData();
    }
  }, [isOpen, selectedInvoiceNumbers]);

  const fetchCommissionData = async () => {
    setLoading(true);
    try {
      const payload = {
        invoice_numbers: selectedInvoiceNumbers,
      };
      
      console.log("📤 Calling calculateBulkCommission API with payload:", payload);
      
      const response = await calculateBulkCommission(payload);
      
      console.log("📥 API Response:", response);
      
      if (response?.status === 200) {
        const data = response?.data?.result;
        setCommissionData(data);
        console.log("✅ Commission data loaded:", data);
      } else {
        toast.error("Failed to calculate commission");
      }
    } catch (error) {
      console.error("❌ Error fetching commission data:", error);
      toast.error(error?.response?.data?.error || "Failed to fetch commission data");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!commissionData) return;

    setProcessing(true);
    setErrorMessage("");

    try {
      console.log("💳 Processing payment for invoices:", selectedInvoiceNumbers);
      console.log("💰 Commission data:", commissionData);
      
      // Extract document IDs from documentTxnList, excluding cancelled docs
      const documentIds = commissionData?.documentTxnList
        ?.filter(doc => doc.status.toUpperCase() === "ACTIVE")
        .map(doc => doc.document_id) || [];
      
      console.log("📋 Extracted document IDs (non-cancelled):", documentIds);
      
      const payload = {
        document_ids: documentIds,
        invoice_numbers: selectedInvoiceNumbers,
        partnership_id,
        role_id,
      };

      console.log("📤 Payment API Payload:", payload);

        const response = await verifyInvoiceOtpNormalWallet(payload);

        console.log("📥 Payment API Response:", response);

        if (response?.data?.result?.paymentResponse?.payuUrl && response?.data?.result?.paymentResponse?.payUPayload) {
          const payuUrl = response.data.result.paymentResponse.payuUrl;
          const payuPayload = response.data.result.paymentResponse.payUPayload;

          setTxnid(payuPayload.txnid); // Set txnid to trigger WebSocket connection
          toast.success("Redirecting to payment gateway...");
          setRedirecting(true);

          // Create a dynamic form for redirection with full payload
          const form = document.createElement("form");
          form.method = "POST";
          form.action = payuUrl;
          
          if (USE_V2_WEBSOCKET) {
              form.target = "_blank";
              toast.info("Payment gateway opened in a new tab. Please complete the payment there.");
          }

          // Append all payload parameters as hidden inputs
          Object.entries(payuPayload).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit(); // Submit the form to PayU
        } else {
          throw new Error("Failed to process payment.");
        }
    } catch (error) {
      console.error("❌ Payment processing error:", error);
      const errorMsg = error.response?.data?.error || "Failed to process payment.";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

   const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  return (
    <Fragment>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" centered>

        {activeStep === 0 && (
          <>
            <ModalHeader toggle={toggle} className="pb-0 mb-0 border-0">
              {loading ? "Fetching Invoice Details..." : "Invoice Details"}
            </ModalHeader>
            <ModalBody>
              {loading ? (
                <GetLoader />
              ) : errorMessage ? (
                <div className="text-center text-danger">
                  <h5>{errorMessage}</h5>
                  <Button color="danger" onClick={toggle}>
                    Close
                  </Button>
                </div>
              ) : commissionData ? (
                <>
                  <Row>
                    <Col xl="12" md="12" className="pl-2 pr-2">
                      <table
                        borderless
                        style={{
                          borderCollapse: "collapse",
                          width: "100%",
                        }}
                      >
                        <thead
                          style={{
                            background: "#f2f2f2",
                            fontSize: "14px",
                          }}
                        >
                          <tr>
                            <th
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {"Particulars"}
                            </th>
                            <th
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {"Amount (INR)"}
                            </th>
                          </tr>
                        </thead>
                        <tbody style={{ fontSize: "14px" }}>
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                              }}
                            >
                              Total Documents
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {commissionData?.total_documents || 0}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                              }}
                            >
                              {`Total Cost`}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {convertToRupees(
                                commissionData?.total_premium_amount || 0,
                                true
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                              }}
                            >
                              {`Total Commission`}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {convertToRupees(
                                commissionData?.total_commission_amount || 0,
                                true
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                              }}
                            >
                              {`Total Amount`}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {convertToRupees(
                                commissionData?.base_amount_payable || 0,
                                true
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                              }}
                            >
                              {"GST @ 18%"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                              }}
                            >
                              {convertToRupees(commissionData?.gst_amount || 0, true)}
                            </td>
                          </tr>



                          {/* Cancelled documents details - only show entire CN section if there are cancelled documents */}
                          {commissionData?.total_cancelled_documents > 0 && (
                            <>
                              <tr>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    textAlign: "left",
                                  }}
                                  className="mb-1"
                                >
                                  {"Invoice Gross Payable"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                  className="mb-2"
                                >
                                  {convertToRupees(
                                    commissionData?.net_payable_amount || 0,
                                    true
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td colSpan="2" style={{ border: "1px solid #ddd", padding: "6px 8px", backgroundColor: "#fff3cd", fontWeight: "bold", fontSize: "12px", color: "#856404" }}>
                                  CN (Cancelled Documents) Details
                                </td>
                              </tr>
                              <tr>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                  Total Cancelled Documents
                                </td>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                                  {commissionData?.total_cancelled_documents}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                  Total Cancelled Cost
                                </td>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                                  {convertToRupees(commissionData?.total_premium_amount_of_cancelled_documents || 0, true)}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                  Total MSC CN
                                </td>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                                  {convertToRupees(commissionData?.total_commission_amount_of_cancelled_documents || 0, true)}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                  CN Gross Payable
                                </td>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                                  {convertToRupees(commissionData?.base_amount_payable_for_cancelled_documents || 0, true)}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                  GST (18%)
                                </td>
                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>
                                  {convertToRupees(commissionData?.gst_amount_for_cancelled_documents || 0)}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                        <tfoot>
                          {commissionData?.total_cancelled_documents > 0 && (
                            <tr>
                              <td
                                style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}
                                className="mb-1"
                              >
                                {"Gross CN Amount"}
                              </td>
                              <td
                                style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}
                                className="mb-2"
                              >
                                {convertToRupees(commissionData?.net_payable_amount_for_cancelled_documents || 0, true)}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "left",
                                fontWeight: "bold",
                              }}
                              className="mb-1"
                            >
                              {"Total Amount Payable"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#28a745",
                              }}
                              className="mb-2"
                            >
                              {convertToRupees(
                                commissionData?.net_payable_amount_for_cancelled_documents
                                  ? commissionData?.net_payable_amount -
                                  commissionData?.net_payable_amount_for_cancelled_documents
                                  : commissionData?.net_payable_amount || 0,
                                true
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-center mt-3">
                      <Button
                        color="success"
                        size="md"
                        onClick={handleNext}
                        disabled={processing || redirecting}
                      >
                        {processing ? "Processing..." : redirecting ? "Redirecting..." : "Send Link"}
                      </Button>
                    </Col>
                    <Col className="text-center mt-3">
                      <Button
                        color="success"
                        size="md"
                        onClick={handlePayment}
                        disabled={processing || redirecting}
                      >
                        {processing ? "Processing..." : redirecting ? "Redirecting..." : "Proceed to Pay"}
                      </Button>
                    </Col>
                  </Row>
                </>
              ) : (
                <div className="alert alert-warning">
                  <i className="fa fa-exclamation-triangle"></i> No commission data available
                </div>
              )}
            </ModalBody>
          </>
        )}

        {activeStep === 1 && (
          <>
            <ModalHeader toggle={toggle}>
              Send Payment Link
            </ModalHeader>

            <ModalBody>
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="email">
                      Email Address <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label for="mobileNumber">
                      Mobile Number <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="tel"
                      id="mobileNumber"
                      name="mobileNumber"
                      placeholder="Enter Mobile Number"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      maxLength={10}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mt-2">
                <Col md={12}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={confirmDetails}
                        onChange={() =>
                          setConfirmDetails(!confirmDetails)
                        }
                      />{" "}
                      I confirm that the above email address and mobile number are
                      correct.
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col className="text-right">
                  <Button
                    color="secondary"
                    className="mr-2"
                    onClick={() => setActiveStep(0)}
                  >
                    Back
                  </Button>

                  <Button
                    color="success"
                    disabled={
                      !confirmDetails ||
                      !email.trim() ||
                      !mobileNumber.trim()
                    }
                    onClick={handleSendLink}
                  >
                    Send Link
                  </Button>
                </Col>
              </Row>
            </ModalBody>
          </>
        )}

      </Modal>
    </Fragment>
  );
};

export default PayBulkInvoiceModal;
